import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import { ApiError, apiFetch } from '../lib/api'
import { PRODUCTS, type ContractResponse, type Order, type Product, type ProductQuota } from '../lib/types'
import { formatDateTime, formatLongDate, parseIsoDate } from '../lib/months'
import { toast } from '../lib/toast'
import { useProductTab } from '../hooks/useProductTab'
import { useProductStatus } from '../hooks/useProductStatus'
import { useMoney } from '../hooks/useMoney'
import { useWriteErrorHandler } from '../hooks/useWriteErrorHandler'
import { Tabs } from '../components/ui/Tabs'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { PageHeader } from '../components/ui/PageHeader'
import { Card, CardHeader, CardBody } from '../components/ui/Card'
import { Kpi, KpiRow } from '../components/ui/Kpi'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { InfoTip } from '../components/ui/InfoTip'
import { QtyStepper } from '../components/ui/QtyStepper'
import { SubHead } from '../components/ui/SubHead'
import { DataTable, type DataTableColumn } from '../components/ui/DataTable'
import { ConfirmPurchaseModal, type SummaryRow } from '../components/ConfirmPurchaseModal'
import { ProductViewGate } from '../components/ProductViewGate'
import { PurchaseForecastChart } from '../components/PurchaseForecastChart'
import { SIM_MONTHS, mcpDeliveredMonths } from '../lib/forecast'

/* Additional Volume (MS-820; prototype `#section-volume`, `renderVolumeCard`,
   `renderOrders`). One "Buy additional volume" card per product: a full-width
   "Volume and balance" strip, then the cart and the purchase simulation side
   by side (spec §24-layout); below it the Order history.

   Trial (Q14, §24f-trial-i): the whole card stays live — stepper, totals,
   projection and simulation — and only Purchase is locked, with a lock glyph
   and a note. Contracted volume and the average read as em dashes, the
   balance is what is left of the trial allowance, and there is no Order
   history (a trial cannot have bought anything). Expired and Not Contracted
   are the preview (MS-819 matrix).

   The bundle's size and price are the account's own (Q3, /quota); when none
   is configured, Purchase is disabled with a short note (an ADR deviation —
   the prototype has no such state). */

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

export function AdditionalVolume() {
  const { t } = useTranslation()
  const { product, setProduct } = useProductTab('additional-volume')
  const status = useProductStatus(product)

  return (
    <PageView>
      <PageHeader title={t('additionalVolume.title')} subtitle={t('additionalVolume.subtitle')} />

      <Tabs value={product} onChange={setProduct} options={PRODUCTS} />

      <PageScrollArea>
        <ProductViewGate status={status} page="additional-volume" product={product}>
          {(view, current) => (
            // Keyed by product: the cart and projection are per tab, as the
            // prototype's per-panel `volState` is.
            <div key={product} className="flex flex-col gap-[18px]">
              <BuyVolumeCard product={product} current={current} trial={view === 'trial'} />
              {view === 'full' && <OrderHistoryCard product={product} />}
            </div>
          )}
        </ProductViewGate>
      </PageScrollArea>
    </PageView>
  )
}

/** The product's contract dates, from /contract. The totals and the
    simulation are capped at the contract end, which only /contract knows:
    until it has answered — or if it fails — a figure would be the uncapped
    12 months the server may not deliver, so a contracted purchase cannot be
    confirmed (`contractUnknown`). A trial's totals are never capped. */
function useContractDates(product: Product, trial: boolean) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['contract'],
    queryFn: () => apiFetch<ContractResponse>('/api/v1/contract'),
  })
  const subscription = data?.subscriptions.find((s) => s.product === product)
  return {
    contractStart: subscription?.contractStartDate ?? null,
    contractEnd: subscription?.contractEndDate ?? null,
    contractUnknown: !trial && (isPending || isError),
    contractFailed: !trial && isError,
  }
}

function BuyVolumeCard({
  product,
  current,
  trial,
}: Readonly<{ product: Product; current: ProductQuota; trial: boolean }>) {
  const { t, i18n } = useTranslation()
  const money = useMoney()
  const queryClient = useQueryClient()
  const onWriteError = useWriteErrorHandler()
  const isMcp = product === 'Mambu MCP'

  // One bundle, not none: the reader came here to buy, and an empty cart
  // simulates nothing. The projection is seeded from the account's own
  // trailing average (Q15) — a trial or a first-month account has none, and
  // starts at zero.
  const [packs, setPacks] = useState(1)
  const [burn, setBurn] = useState(trial ? 0 : (current.avgMonthlyConsumption ?? 0))
  useEffect(() => {
    if (!trial) setBurn(current.avgMonthlyConsumption ?? 0)
  }, [trial, current.avgMonthlyConsumption])

  // Generated when the modal opens, not per click: a double click or a retry
  // of the same confirmation is one purchase (C-5).
  const [requestId, setRequestId] = useState<string | null>(null)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)

  const { contractStart, contractEnd, contractUnknown, contractFailed } = useContractDates(product, trial)

  const bundle =
    current.bundleUnit !== null && current.bundlePrice !== null
      ? { unit: current.bundleUnit, price: Number(current.bundlePrice) }
      : null
  const cartVolume = bundle ? packs * bundle.unit : 0
  // What the purchase actually delivers (Q2): 12 months, or fewer when the
  // contract ends first — the server caps `renews_until` the same way.
  // A trial's simulation is not capped (it has no contract to end), so
  // neither is its total.
  const deliveredMonths = trial ? SIM_MONTHS : mcpDeliveredMonths(new Date(), contractEnd)
  const capped = deliveredMonths < SIM_MONTHS
  const totalOverValidity = cartVolume * deliveredMonths
  const fmt = (n: number) => n.toLocaleString(i18n.language)
  const bundleName = t(isMcp ? 'additionalVolume.bundleName.mcp' : 'additionalVolume.bundleName.agt')

  const purchase = useMutation({
    mutationFn: (body: { bundles: number; requestId: string }) =>
      apiFetch<ProductQuota>(`/api/v1/quota/${encodeURIComponent(product)}/bundles`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      setRequestId(null)
      setPacks(1)
      toast(t('additionalVolume.purchaseConfirmed'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['quota'] }),
        queryClient.invalidateQueries({ queryKey: ['orders', product] }),
        queryClient.invalidateQueries({ queryKey: ['contract'] }),
      ])
    },
    onError: async (error) => {
      // The product's status moved under the page: close, and let the view
      // re-resolve (MS-819 Q9). Anything else stays in the modal, reported.
      if (error instanceof ApiError && error.code.startsWith('status_conflict.')) {
        setRequestId(null)
        await onWriteError(error)
        return
      }
      setPurchaseError(purchaseErrorMessage(error, t))
    },
  })

  function onPurchaseClick() {
    if (packs < 1) {
      toast(t('additionalVolume.chooseAtLeastOne'))
      return
    }
    setPurchaseError(null)
    setRequestId(crypto.randomUUID())
  }

  const rows: SummaryRow[] = bundle
    ? summaryRows({
        t,
        fmt,
        money,
        isMcp,
        bundleName,
        packs,
        cartVolume,
        totalOverValidity,
        bundle,
        capped,
        contractEnd,
        language: i18n.language,
      })
    : []

  const locked = trial || bundle === null || contractUnknown
  const cartInfoKey = isMcp ? 'additionalVolume.cartInfoMcp' : 'additionalVolume.cartInfoAgt'
  const cartInfo = bundle ? t(cartInfoKey, { volume: fmt(bundle.unit) }) : undefined

  return (
    <Card collapsible>
      <CardHeader
        title={t('additionalVolume.buyAdditionalVolume')}
        subtitle={t(isMcp ? 'additionalVolume.addSubtitleMcp' : 'additionalVolume.addSubtitleAgt')}
      />
      <CardBody>
        <SubHead
          label={t('additionalVolume.volumeAndBalance')}
          info={t(isMcp ? 'additionalVolume.balanceInfoMcp' : 'additionalVolume.balanceInfoAgt')}
          infoWidth={340}
        />
        <VolumeKpis current={current} trial={trial} isMcp={isMcp} fmt={fmt} />

        <div className="mt-[22px] pt-[22px] border-t border-[var(--border-light)]">
          <div className="flex gap-[22px] items-start flex-wrap">
            <div className="flex-[1_1_320px] min-w-[300px]" data-testid="vol-cart">
              <SubHead
                label={t('additionalVolume.buyOnDemandVolume')}
                info={cartInfo}
                infoWidth={320}
              />
              <div className="flex gap-6 items-center justify-between pt-0.5 pb-4 border-b border-[var(--border-light)]">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13.5px] mb-2">{bundleName}</div>
                  {bundle && (
                    <div className="flex gap-2 flex-wrap mb-2">
                      <Badge variant="accent">
                        {t(isMcp ? 'additionalVolume.volumeBadgeMcp' : 'additionalVolume.volumeBadgeAgt', {
                          volume: fmt(bundle.unit),
                        })}
                      </Badge>
                      <Badge className="!bg-[rgba(128,128,128,.14)] !text-[var(--text-secondary)]">
                        {t(isMcp ? 'additionalVolume.validity.mcp' : 'additionalVolume.validity.agt')}
                      </Badge>
                    </div>
                  )}
                </div>
                {bundle && (
                  <div className="flex items-center gap-4 whitespace-nowrap">
                    <div className="text-[13px] text-[var(--text-secondary)]">
                      <Trans
                        i18nKey="additionalVolume.eachPrice"
                        values={{ price: money(bundle.price) }}
                        components={{ strong: <strong className="font-bold text-[var(--text-primary)]" /> }}
                      />
                    </div>
                    <QtyStepper value={packs} onChange={setPacks} min={0} editable label={bundleName} />
                  </div>
                )}
              </div>
              {bundle && (
                <CartTotals
                  isMcp={isMcp}
                  capped={capped}
                  cartVolume={fmt(cartVolume)}
                  totalOverValidity={contractUnknown ? t('common.dash') : fmt(totalOverValidity)}
                  totalPrice={money(packs * bundle.price)}
                />
              )}
              <PurchaseBar
                trial={trial}
                locked={locked}
                notConfigured={bundle === null}
                contractFailed={contractFailed}
                onPurchase={onPurchaseClick}
              />
            </div>

            <div className="flex-[2_1_420px] min-w-[340px]">
              <PurchaseForecastChart
                product={product}
                quota={current}
                trial={trial}
                // Not simulated until the contract end that caps it is known.
                cartVolume={contractUnknown ? 0 : cartVolume}
                burn={burn}
                onBurnChange={setBurn}
                contractStart={contractStart}
                contractEnd={contractEnd}
              />
            </div>
          </div>
        </div>
        {requestId !== null && (
          <ConfirmPurchaseModal
            product={product}
            rows={rows}
            pending={purchase.isPending}
            error={purchaseError}
            onConfirm={() => purchase.mutate({ bundles: packs, requestId })}
            onCancel={() => {
              setRequestId(null)
              setPurchaseError(null)
            }}
          />
        )}
      </CardBody>
    </Card>
  )
}

const PURCHASE_ERROR_KEYS = {
  'purchase.bundle_not_configured': 'additionalVolume.notConfigured',
  'purchase.client_outdated': 'confirmPurchase.error.clientOutdated',
} as const

function purchaseErrorMessage(error: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  // A network failure, or the server failing on its side: the purchase may
  // or may not have been recorded, and retrying this confirmation (same
  // requestId) is safe either way.
  if (!(error instanceof ApiError) || error.status >= 500) return t('confirmPurchase.error.unavailable')
  if (error.code in PURCHASE_ERROR_KEYS) {
    return t(PURCHASE_ERROR_KEYS[error.code as keyof typeof PURCHASE_ERROR_KEYS])
  }
  if (error.status === 400 && error.field === 'bundles') return t('confirmPurchase.error.tooMany')
  return t('confirmPurchase.error.generic')
}

interface SummaryInput {
  t: ReturnType<typeof useTranslation>['t']
  fmt: (n: number) => string
  money: (n: number) => string
  isMcp: boolean
  bundleName: string
  packs: number
  cartVolume: number
  totalOverValidity: number
  bundle: { unit: number; price: number }
  capped: boolean
  contractEnd: string | null
  language: string
}

/** The confirmation modal's summary rows (prototype `openVolumeConfirm`). */
function summaryRows(input: Readonly<SummaryInput>): SummaryRow[] {
  const { t, fmt, money, isMcp, bundleName, packs, cartVolume, totalOverValidity, bundle, capped, contractEnd } = input
  const validityMcp =
    capped && contractEnd !== null
      ? t('confirmPurchase.summary.validityMcpCapped', { date: formatLongDate(contractEnd, input.language) })
      : t('confirmPurchase.summary.validityMcp')
  const productRows: SummaryRow[] = isMcp
    ? [
        { label: t('confirmPurchase.summary.invocationsPerMonth'), value: `+${fmt(cartVolume)}` },
        {
          label: capped
            ? t('confirmPurchase.summary.totalUntilContractEnd')
            : t('confirmPurchase.summary.totalOverValidity'),
          value: `+${fmt(totalOverValidity)}`,
        },
        { label: t('confirmPurchase.summary.validity'), value: validityMcp },
      ]
    : [
        { label: t('confirmPurchase.summary.creditsAdded'), value: `+${fmt(cartVolume)}` },
        { label: t('confirmPurchase.summary.validity'), value: t('confirmPurchase.summary.validityAgt') },
      ]
  return [
    { label: bundleName, value: fmt(packs) },
    ...productRows,
    { label: t('confirmPurchase.summary.totalPrice'), value: money(packs * bundle.price) },
  ]
}

/** The "Volume and balance" strip: contracted volume, balance, average. */
function VolumeKpis({
  current,
  trial,
  isMcp,
  fmt,
}: Readonly<{ current: ProductQuota; trial: boolean; isMcp: boolean; fmt: (n: number) => string }>) {
  const { t } = useTranslation()
  const contracted = trial || current.contractedVolume === null ? null : fmt(current.contractedVolume)
  const balance = current.balanceRemaining === null ? null : fmt(current.balanceRemaining)
  const average = trial || current.avgMonthlyConsumption === null ? null : fmt(current.avgMonthlyConsumption)
  return (
    <div className="[&>div]:mb-0">
      <KpiRow>
        <Kpi
          label={t('additionalVolume.kpi.contractedVolume')}
          value={contracted}
          unit={` ${t(isMcp ? 'common.perMonth' : 'common.perYear')}`}
        />
        <Kpi label={t('additionalVolume.kpi.balanceRemaining')} value={balance} />
        <Kpi
          label={
            <span className="inline-flex items-center">
              {t('additionalVolume.kpi.avgMonthly')}
              <InfoTip text={t('additionalVolume.avgInfo')} placement="below" align="left" width={320} />
            </span>
          }
          value={average}
        />
      </KpiRow>
    </div>
  )
}

/** The cart's totals, right-aligned under the bundle line. */
function CartTotals({
  isMcp,
  capped,
  cartVolume,
  totalOverValidity,
  totalPrice,
}: Readonly<{ isMcp: boolean; capped: boolean; cartVolume: string; totalOverValidity: string; totalPrice: string }>) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-end gap-1 mt-3.5 mx-0.5 text-[13px] text-[var(--text-secondary)]">
      <div className="flex gap-2.5 items-baseline">
        <span>{t(isMcp ? 'additionalVolume.totalMcp' : 'additionalVolume.totalAgt')}</span>
        <strong className="font-bold text-[var(--text-primary)]">{cartVolume}</strong>
      </div>
      {isMcp && (
        <div className="flex gap-2.5 items-baseline">
          <span>
            {capped ? t('additionalVolume.totalUntilContractEnd') : t('additionalVolume.totalOverValidity')}
          </span>
          <strong className="font-bold text-[var(--text-primary)]">{totalOverValidity}</strong>
        </div>
      )}
      <div className="flex gap-2.5 items-baseline">
        <span>{t('additionalVolume.totalPrice')}</span>
        <strong className="font-bold text-[var(--text-primary)]">{totalPrice}</strong>
      </div>
    </div>
  )
}

type LockNote =
  | 'additionalVolume.lockedTrial'
  | 'additionalVolume.notConfigured'
  | 'additionalVolume.contractUnavailable'

/** Purchase, with a note for each reason it cannot be used: a trial, no
    bundle configured (shown on a trial too — otherwise a trial account
    without a bundle sees an empty cart with no reason given), or the
    contract dates failing to load. Locked with no note only while /contract
    is still loading. */
function PurchaseBar({
  trial,
  locked,
  notConfigured,
  contractFailed,
  onPurchase,
}: Readonly<{
  trial: boolean
  locked: boolean
  notConfigured: boolean
  contractFailed: boolean
  onPurchase: () => void
}>) {
  const { t } = useTranslation()
  const notes: LockNote[] = []
  if (trial) notes.push('additionalVolume.lockedTrial')
  if (notConfigured) notes.push('additionalVolume.notConfigured')
  if (contractFailed) notes.push('additionalVolume.contractUnavailable')
  return (
    <div className="flex justify-end items-center gap-3.5 mt-3.5">
      {notes.length > 0 && (
        <div className="flex flex-col items-end gap-1">
          {notes.map((key) => (
            <span
              key={key}
              role={key === 'additionalVolume.contractUnavailable' ? 'alert' : undefined}
              className="inline-flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)]"
            >
              {key === 'additionalVolume.lockedTrial' && <LockIcon />}
              {t(key)}
            </span>
          ))}
        </div>
      )}
      <Button
        variant="primary"
        disabled={locked}
        onClick={onPurchase}
        className={`!px-6 py-[9px] ${locked ? 'disabled:!opacity-[.55]' : ''}`}
      >
        {trial && <LockIcon />}
        {t('additionalVolume.purchase')}
      </Button>
    </div>
  )
}

/** Order history (§24h; prototype `renderOrders` + `ORD_COLS`): On Demand
 * purchases only, newest first, sortable and paged. Each row is described by
 * what ONE bundle contains — the count is its own column — so a two-bundle
 * order still reads "… · 320,000 invocations / mo" (§24h-desc, AC3), built
 * through t() rather than stored English (Q10). */
function OrderHistoryCard({ product }: Readonly<{ product: Product }>) {
  const { t, i18n } = useTranslation()
  const money = useMoney()
  const isMcp = product === 'Mambu MCP'
  const { data: orders, isPending, isError, refetch } = useQuery({
    queryKey: ['orders', product],
    queryFn: () => apiFetch<Order[]>(`/api/v1/orders?product=${encodeURIComponent(product)}`),
  })
  const bundleName = t(isMcp ? 'additionalVolume.bundleName.mcp' : 'additionalVolume.bundleName.agt')
  const dash = t('common.dash')

  const columns = useMemo<DataTableColumn<Order>[]>(() => {
    // "/ mo" only for an MCP bundle that renews monthly; a backfilled legacy
    // MCP order was one pot, so it is described without the monthly wording.
    const descriptionKey = (o: Order) => {
      if (!isMcp) return 'additionalVolume.bundleDescription.agt'
      return o.renewsMonthly ? 'additionalVolume.bundleDescription.mcp' : 'additionalVolume.bundleDescription.mcpOnce'
    }
    const describe = (o: Order) =>
      t(descriptionKey(o), { name: bundleName, volume: o.bundleUnit.toLocaleString(i18n.language) })
    const instant = (o: Order) =>
      o.purchasedAt === null ? parseIsoDate(o.purchaseDate).getTime() : new Date(o.purchasedAt).getTime()
    const buyer = (o: Order) => o.byName ?? o.byEmail ?? ''
    return [
      {
        key: 'ref',
        label: t('additionalVolume.orderHistory.reference'),
        render: (o) => o.reference ?? dash,
        sortValue: (o) => o.reference ?? '',
        nowrap: true,
      },
      {
        key: 'date',
        label: t('additionalVolume.orderHistory.dateTime'),
        render: (o) =>
          o.purchasedAt === null
            ? formatLongDate(o.purchaseDate, i18n.language)
            : formatDateTime(o.purchasedAt, i18n.language),
        sortValue: instant,
        nowrap: true,
      },
      { key: 'desc', label: t('additionalVolume.orderHistory.description'), render: describe, sortValue: describe },
      {
        key: 'by',
        label: t('additionalVolume.orderHistory.purchasedBy'),
        render: (o) => buyer(o) || dash,
        sortValue: buyer,
      },
      {
        key: 'channel',
        label: t('additionalVolume.orderHistory.channel'),
        render: () => <Badge variant="accent">{t('additionalVolume.orderHistory.onDemand')}</Badge>,
        sortValue: () => 0,
      },
      {
        key: 'qty',
        label: t('additionalVolume.orderHistory.bundles'),
        render: (o) => o.bundles.toLocaleString(i18n.language),
        sortValue: (o) => o.bundles,
        align: 'right',
      },
      {
        key: 'unitPrice',
        label: t('additionalVolume.orderHistory.pricePerBundle'),
        render: (o) => money(Number(o.unitPrice)),
        sortValue: (o) => Number(o.unitPrice),
        align: 'right',
        nowrap: true,
      },
      {
        key: 'amount',
        label: t('additionalVolume.orderHistory.totalAmount'),
        render: (o) => money(Number(o.cost)),
        sortValue: (o) => Number(o.cost),
        align: 'right',
        nowrap: true,
      },
      {
        key: 'invoiceOn',
        label: t('additionalVolume.orderHistory.estimatedInvoiceDate'),
        info: t('additionalVolume.orderHistory.estimatedInvoiceInfo'),
        render: (o) =>
          o.estimatedInvoiceDate === null ? dash : formatLongDate(o.estimatedInvoiceDate, i18n.language),
        sortValue: (o) => (o.estimatedInvoiceDate === null ? 0 : parseIsoDate(o.estimatedInvoiceDate).getTime()),
        nowrap: true,
      },
    ]
  }, [t, i18n.language, isMcp, bundleName, dash, money])

  let body
  if (isPending) {
    body = <output className="block text-[12.5px] text-[var(--text-tertiary)]">{t('common.loading')}</output>
  } else if (isError) {
    body = (
      <div role="alert" className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-[12.5px] text-[var(--danger)]">{t('common.loadError')}</div>
        <Button
          variant="ghost"
          onClick={() => refetch()}
        >
          {t('common.retry')}
        </Button>
      </div>
    )
  } else if (orders.length === 0) {
    body = <div className="text-[12.5px] text-[var(--text-tertiary)]">{t('additionalVolume.orderHistory.empty')}</div>
  } else {
    body = (
      <DataTable
        rows={orders}
        columns={columns}
        rowKey={(o) => o.id}
        defaultSort={{ key: 'date', dir: 'desc' }}
        showing={(from, to, total) => t('additionalVolume.orderHistory.showing', { from, to, total })}
      />
    )
  }

  return (
    <Card collapsible>
      <CardHeader
        title={t('additionalVolume.orderHistory.title')}
        subtitle={t('additionalVolume.orderHistory.subtitle')}
      />
      <CardBody>{body}</CardBody>
    </Card>
  )
}
